import type { FormLocation } from '../utils/trackingUtils';
// Wecall Email Service
// Handles sending wecall form emails via Brevo API

import { emailConfig } from '../config/emailConfig';
import { LOGO_URL } from './logoConstants';
import { processEmailTemplate } from './emailTemplateService';

export interface EmailResult {
    success: boolean;
    messageId?: string;
    error?: string;
}

export interface WecallFormData {
    name: string;
    surname: string;
    phone: string;
    email?: string;
    serviceSelection?: string;
    wantedDay?: string;
    wantedSlot?: string;
    notes?: string;
    form_location?: FormLocation;
}

// Service mapping
const servicesMap: Record<string, string> = {
    'workshop-services-pack': 'Pacote de Serviços de Oficina',
    'air-conditioning': 'Ar Condicionado',
    'headlight-restoration': 'Restauração de Faróis',
    'battery-service': 'Serviço de Bateria',
    'brake-service': 'Serviço de Travões',
    'oil-change': 'Mudança de Óleo e Filtros',
    'engine-repair': 'Reparação de Motor',
    'tire-services': 'Serviços de Pneus e Rodas',
    'diagnostic-test': 'Teste de Diagnóstico',
    'inspection': 'Inspeção',
    'general-mechanics': 'Mecânica Geral',
    'alignment': 'Alinhamento',
    'shock-absorbers': 'Amortecedores',
    'other': 'Outro Serviço'
};

/**
 * Get service name in Portuguese
 */
function getServiceName(serviceSelection: string | undefined): string {
    if (!serviceSelection) return 'Não especificado';
    return servicesMap[serviceSelection] || serviceSelection;
}

/**
 * Get wanted time string
 */
function getWantedTime(wantedDay?: string, wantedSlot?: string): string {
    if (wantedDay && wantedSlot) {
        return `${wantedDay} ${wantedSlot}`;
    }
    return wantedDay || wantedSlot || 'Não especificado';
}

// Single email interface for batch sending
interface SingleEmailPayload {
    to: { email: string; name?: string };
    subject: string;
    htmlContent: string;
    sender?: { name: string; email: string };
    attachments?: Array<{ name: string; content: string }>;
}

// Batch email result
interface BatchEmailResult {
    success: boolean;
    results?: Array<{ success: boolean; messageId?: string; error?: string }>;
    summary?: { total: number; sent: number; failed: number };
    error?: string;
}

/**
 * Get reCAPTCHA token from the page
 */
function getRecaptchaToken(): string {
    try {
        if (typeof (window as any).grecaptcha !== 'undefined') {
            const token = (window as any).grecaptcha.getResponse();
            if (token) {
                return token;
            }
        }
        
        // Fallback: check hidden textarea
        const textarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
        if (textarea && textarea.value) {
            return textarea.value;
        }
    } catch (error) {
    }
    
    return '';
}

/**
 * Send batch emails via Firebase Cloud Function
 */
async function sendBatchEmailsViaBrevo(
    emails: SingleEmailPayload[],
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        const token = recaptchaToken || getRecaptchaToken();
        if (!token) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const emailsWithSender = emails.map(email => ({
            ...email,
            sender: email.sender || {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            }
        }));

        const payload = {
            emails: emailsWithSender,
            recaptchaToken: token
        };


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { success: true };
        }

        return {
            success: result.success,
            results: result.results,
            summary: result.summary
        };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send email via Firebase Cloud Function
 * The Cloud Function handles Brevo API credentials securely
 */
async function sendEmailViaBrevo(
    to: { email: string; name?: string },
    subject: string,
    htmlContent: string
): Promise<EmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        // Get reCAPTCHA token
        const recaptchaToken = getRecaptchaToken();
        if (!recaptchaToken) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const payload = {
            sender: {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            },
            to,
            subject,
            htmlContent,
            recaptchaToken
        };


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { messageId: 'sent' };
        }

        return { success: true, messageId: result.messageId };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send wecall notification email to admin
 */
export async function sendWecallAdminEmail(
    formData: WecallFormData,
    companyName: string = 'Roady Leiria'
): Promise<EmailResult> {
    try {

        const serviceName = getServiceName(formData.serviceSelection);
        const wantedTime = getWantedTime(formData.wantedDay, formData.wantedSlot);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            service_name: serviceName,
            wanted_time: wantedTime,
            notes: formData.notes || '',
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            email_style: formData.email ? '' : 'display: none;',
            service_style: formData.serviceSelection ? '' : 'display: none;',
            wanted_time_style: wantedTime !== 'Não especificado' ? '' : 'display: none;',
            notes_style: formData.notes ? '' : 'display: none;',
            logo_url: LOGO_URL
        };

        // Process admin template
        const htmlContent = await processEmailTemplate('wecall-admin.html', variables);

        const subject = `Pedido Ligamos de ${formData.name} ${formData.surname}`;

        const result = await sendEmailViaBrevo(
            {
                email: emailConfig.BREVO_TO_EMAIL_WECALL,
                name: `${companyName} Admin`
            },
            subject,
            htmlContent
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

// Get company address helper
function getCompanyAddress(name: string): string {
    if (name.includes('Valongo')) return 'Tv. António Augusto Castro Paupério 36, Valongo';
    if (name.includes('Leiria')) return 'Rua da Baquelite 204, Leiria';
    if (name.includes('Oliveira')) return 'Av. D. Maria I - Lugar de Aldas, 3720-300 Oliveira de Azeméis';
    if (name.includes('São Domingos')) return 'Avenida Antero Brotas – Sítio do Esteval, 2870-122 São Domingos de Rana';
    if (name.includes('Malveira')) return 'Alto da Lagoa, 2665-297 Malveira, Portugal';
    return 'Portugal';
}

/**
 * Prepare wecall admin email payload (for batch sending)
 */
async function prepareWecallAdminEmail(
    formData: WecallFormData,
    companyName: string = 'Roady'
): Promise<SingleEmailPayload | null> {
    try {
        const serviceName = getServiceName(formData.serviceSelection);
        const wantedTime = getWantedTime(formData.wantedDay, formData.wantedSlot);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            service_name: serviceName,
            wanted_time: wantedTime,
            notes: formData.notes || '',
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            email_style: formData.email ? '' : 'display: none;',
            service_style: formData.serviceSelection ? '' : 'display: none;',
            wanted_time_style: wantedTime !== 'Não especificado' ? '' : 'display: none;',
            notes_style: formData.notes ? '' : 'display: none;',
            logo_url: LOGO_URL
        };

        const htmlContent = await processEmailTemplate('wecall-admin.html', variables);
        const subject = `Pedido Ligamos de ${formData.name} ${formData.surname}`;

        return {
            to: { email: emailConfig.BREVO_TO_EMAIL_WECALL, name: `${companyName} Admin` },
            subject,
            htmlContent
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Prepare wecall user email payload (for batch sending)
 */
async function prepareWecallUserEmail(
    formData: WecallFormData,
    companyName: string = 'Roady',
    language: string = 'pt'
): Promise<SingleEmailPayload | null> {
    try {
        if (!formData.email) {
            return null;
        }

        const serviceName = getServiceName(formData.serviceSelection);
        const wantedTime = getWantedTime(formData.wantedDay, formData.wantedSlot);
        const wantedTimeDisplay = language === 'en' && wantedTime === 'Não especificado' 
            ? 'Not specified' 
            : wantedTime;

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            service_name: serviceName,
            wanted_time: wantedTimeDisplay,
            year: new Date().getFullYear().toString(),
            service_style: formData.serviceSelection ? '' : 'display: none;',
            email_style: formData.email ? '' : 'display: none;',
            wanted_time_style: (language === 'en' && wantedTimeDisplay === 'Not specified') || (language === 'pt' && wantedTime === 'Não especificado') ? 'display: none;' : '',
            logo_url: LOGO_URL,
            wecall_email: emailConfig.BREVO_TO_EMAIL_WECALL || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName)
        };

        const templateName = language === 'en' 
            ? 'wecall-user-en.html' 
            : 'wecall-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Request Confirmation - ${companyName}`
            : `Confirmação de Pedido - ${companyName}`;

        return {
            to: { email: formData.email, name: `${formData.name} ${formData.surname}` },
            subject,
            htmlContent
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Send both wecall emails in a single batch (admin + user confirmation)
 */
export async function sendWecallEmailsBatch(
    formData: WecallFormData,
    companyName: string = 'Roady',
    language: string = 'pt',
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {

        const emails: SingleEmailPayload[] = [];

        const adminEmail = await prepareWecallAdminEmail(formData, companyName);
        if (adminEmail) {
            emails.push(adminEmail);
        }

        const userEmail = await prepareWecallUserEmail(formData, companyName, language);
        if (userEmail) {
            emails.push(userEmail);
        }

        if (emails.length === 0) {
            throw new Error('No emails to send');
        }

        const result = await sendBatchEmailsViaBrevo(emails, recaptchaToken);

        if (result.success) {
        } else {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send wecall confirmation email to user
 */
export async function sendWecallUserEmail(
    formData: WecallFormData,
    companyName: string = 'Roady Leiria',
    language: string = 'pt'
): Promise<EmailResult> {
    try {
        if (!formData.email) {
            return { success: true }; // No email provided, skip
        }


        const serviceName = getServiceName(formData.serviceSelection);
        const wantedTime = getWantedTime(formData.wantedDay, formData.wantedSlot);
        const wantedTimeDisplay = language === 'en' && wantedTime === 'Não especificado' 
            ? 'Not specified' 
            : wantedTime;

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            service_name: serviceName,
            wanted_time: wantedTimeDisplay,
            year: new Date().getFullYear().toString(),
            service_style: formData.serviceSelection ? '' : 'display: none;',
            email_style: formData.email ? '' : 'display: none;',
            wanted_time_style: (language === 'en' && wantedTimeDisplay === 'Not specified') || (language === 'pt' && wantedTime === 'Não especificado') ? 'display: none;' : '',
            logo_url: LOGO_URL,
            wecall_email: emailConfig.BREVO_TO_EMAIL_WECALL || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName)
        };

        // Process user template based on language
        const templateName = language === 'en' 
            ? 'wecall-user-en.html' 
            : 'wecall-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Request Confirmation - ${companyName}`
            : `Confirmação de Pedido - ${companyName}`;

        const result = await sendEmailViaBrevo(
            {
                email: formData.email,
                name: `${formData.name} ${formData.surname}`
            },
            subject,
            htmlContent
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}
